import {
  AfterViewInit,
  Component,
  ElementRef, EventEmitter,
  Injectable,
  OnInit, Output,
  ViewChild
} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {UserService} from "../service/user.service";
import {ALPHABETIC_STRING_PATTERN, EMAIL_PATTERN} from "../constants/validation.constants";
import {UserUpdateRequest} from "../interface/userUpdateRequest";
import intlTelInput from "intl-tel-input";
import {UserResponse} from "../interface/userResponse";
import {Router} from "@angular/router";
import {of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MESSAGE_TIMEOUT} from "../constants/global.constants";
import {UserCardUtil} from "../utilityClass/userCardUtil";
import {UserImageResponse} from "../interface/userImageResponse";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {ProfileImageCropperDialogComponent} from "../profile-image-cropper-dialog/profile-image-cropper-dialog.component";


@Component({
  selector: 'app-profile-card',
  templateUrl: './profile-card.component.html',
  styleUrl: './profile-card.component.scss'
})
@Injectable({
  providedIn: 'root'
})
export class ProfileCardComponent implements OnInit, AfterViewInit {
  user: UserResponse | any
  form: FormGroup | any;
  iti: intlTelInput.Options | any;
  invalidPhoneNumber: boolean = false;
  phone: ElementRef<HTMLInputElement> | any;
  editReady = false;
  tempProfileImageUrl: string | null = null;
  currentProfileImage: any;
  showCameraIcon = true;
  tempProfileImage: File | null = null;
  currentProfileImageUrl: string | null = null;
  deleteProfileImageRequested = false;
  errorMessage: string | null = null;
  emailIsEdited: boolean = false
  isDeleted: boolean = false
  emailExists: boolean = false

  @Output() updatedProfileImageUrl: EventEmitter<string | null> = new EventEmitter<string | null>();
  @ViewChild('phoneInput') set phoneInput(value: ElementRef<HTMLInputElement>) {
    this.phone = value;
  }

  @ViewChild('saveButton') saveButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('cancelButton') cancelButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('updateIcon') updateIcon!: ElementRef<HTMLOrSVGImageElement>;
  @ViewChild('cardTitle') cardTitle!: ElementRef<HTMLTitleElement>;
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  constructor(
    private userService: UserService,
    private router: Router,
    private snackBar: MatSnackBar,
    private userCardUtil: UserCardUtil,
    private dialog: MatDialog,) {
  }

  public getUserCardUtil(): UserCardUtil {
    return this.userCardUtil;
  }

  ngOnInit(): void {
    this.userService.currentUser.subscribe(response => {
      this.user = response
    })
    if (!this.user) {
      alert('User not present');
      return;
    }
    this.initializeForm();
    this.fetchProfileImage(this.user.id);
    this.userService.updatedProfileImageUrl.emit(this.currentProfileImage);
  }

  ngAfterViewInit(): void {
    const phoneInput: HTMLInputElement | any = this.phone?.nativeElement;
    if (phoneInput) {
      this.iti = intlTelInput(phoneInput, {
        initialCountry: this.user.countryIso,
        autoPlaceholder: 'polite',
        separateDialCode: true,
        utilsScript: 'node_modules/intl-tel-input/build/js/utils.js',
      } as intlTelInput.Options);
    }
  }

  initializeForm(): void {
    this.form = new FormGroup({
      name: new FormControl({
        value: this.user?.name,
        disabled: true
      }, [Validators.required, Validators.pattern(ALPHABETIC_STRING_PATTERN)]),
      surname: new FormControl({
        value: this.user?.surname,
        disabled: true
      }, [Validators.required, Validators.pattern(ALPHABETIC_STRING_PATTERN)]),
      email: new FormControl(this.user?.email, [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
      phoneNumber: new FormControl(this.user?.phoneNumber, Validators.required),
    }, {updateOn: 'blur'});
  }

  onAllowUpdate(): void {
    this.editReady = true;
  }

  fetchProfileImage(userId: number): void {
    if (this.user.profilePic !== null) {
      this.userService.getProfileImage(BigInt(userId)).subscribe((imageData) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.currentProfileImage = e.target?.result as string;
          this.currentProfileImageUrl = this.currentProfileImage;
          this.userService.setCurrentProfileImageUrl(this.currentProfileImageUrl);
        };
        reader.readAsDataURL(imageData);
      });
    }
  }

  onCancelProfileEdit() {
    this.onCancel();
  }

  onCancel(): void {
    this.fetchProfileImage(this.user.id);
    this.tempProfileImage = null;
    this.currentProfileImage = null;
    this.editReady = false;
    this.invalidPhoneNumber = false;
    this.resetErrorMessage();
    this.initializeForm();
    const input = this.imageInput.nativeElement;
    input.value = '';
    this.router.navigateByUrl(this.router.url);
  }



  get profileEditControl(): any {
    return this.form.controls;
  }

  resetErrorMessage(): void {
    this.errorMessage = '';
    this.emailExists = false;
  }

  validatePhoneNumber(): void {
    this.invalidPhoneNumber = !this.iti.isValidNumber();
  }

  openImageInput(): void {
    this.imageInput.nativeElement.click();
    this.showCameraIcon = true;
  }

  onProfileImageChange(event: any): void {
    this.isDeleted = false;
    const dialogConfig = this.getDialogConfig({imageChangedEvent: event})
    const dialogRef = this.dialog.open(ProfileImageCropperDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.tempProfileImage = result;
        this.currentProfileImage = URL.createObjectURL(result);
      }
    });
      this.tempProfileImage = null;
  }

  deleteProfileImage(): void {
    this.isDeleted = true
    this.deleteProfileImageRequested = true;
    this.currentProfileImage = null;
    this.tempProfileImage = null;
  }

  isDefaultImage(): boolean {
    return !this.tempProfileImageUrl && !this.currentProfileImage;
  }

  navigateToLogin(): void {
    this.userService.logout();
  }

  private getDialogConfig(data: any): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = data;
    dialogConfig.maxWidth = '700px';
    dialogConfig.width = 'auto';
    return dialogConfig;
  }

}
